import type {
	FilterGroupConfig,
	FilterOrFilterGroupConfig,
	FiltersConfig,
	IndexedData,
	IndexedDatum,
	IndexedFund,
	IndexedProfile,
	IndexedShareclass,
} from "hsl-core/FundExplorer";

export default function pruneFilters(
	data: IndexedData,
	filters: FiltersConfig["filters"],
) {
	const shareClasses: IndexedShareclass[] = [];
	const funds: IndexedFund[] = [];
	const profiles: IndexedProfile[] = [];

	for (const entity of Object.values(data)) {
		if (entity.type === "shareclass") {
			shareClasses.push(entity);
		} else if (entity.type === "fund") {
			funds.push(entity);
		} else if (entity.type === "profile") {
			profiles.push(entity);
		}
	}

	const entitiesByType: Record<string, IndexedDatum[]> = {
		fund: funds,
		shareclass: shareClasses,
		profile: profiles,
	};

	// Helper function: Normalize a value to an array of lowercased strings
	function normalize(value: unknown): string[] {
		return Array.isArray(value)
			? value.map((v) => `${v}`.toLowerCase())
			: [`${value}`.toLowerCase()];
	}

	// Helper function: Check if optionValue matches entityValue
	function isOptionValid(optionValue: unknown, entityValue: unknown): boolean {
		const normalizedOption = normalize(optionValue);
		const normalizedEntity = normalize(entityValue);
		return normalizedOption.some((ov) => normalizedEntity.includes(ov));
	}

	function pruneFiltersRecursive(
		filtersList: FilterOrFilterGroupConfig[],
	): FilterOrFilterGroupConfig[] {
		return filtersList.reduce((prunedFilters, filter) => {
			if (filter.type === "group") {
				// Recursively prune inner filters
				const prunedInnerFilters = pruneFiltersRecursive(filter.filters);
				// Only include group if it has non-empty inner filters
				if (prunedInnerFilters.length > 0) {
					prunedFilters.push({
						...filter,
						filters: prunedInnerFilters,
					} as FilterGroupConfig);
				}
			} else if (filter.type === "single") {
				const prunedOptions = filter.options.filter((option) => {
					const entityKey = option.entityKey ?? filter.entityKey;
					const [entityType, prop] = entityKey.split(".");
					const entityList = entitiesByType[entityType] || [];
					// Keep the option if at least one entity matches
					return entityList.some((entity) =>
						isOptionValid(option.value, entity.data[prop]),
					);
				});
				// Only include single filter if enough options remain
				if (prunedOptions.length >= 2) {
					prunedFilters.push({ ...filter, options: prunedOptions });
				}
			}
			return prunedFilters;
		}, [] as FilterOrFilterGroupConfig[]);
	}

	return pruneFiltersRecursive(filters);
}
